import createAction from 'Store/utils/createAction';
import {
  CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
  CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART,
  CUSTOMER_ACCOUNT_GET_BOOKINGS_CART,
} from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { attributesToString } from 'Utils/graphql-client/graphqlClient.utils';
import { getBookingPrecision } from '@clubmed/domains-service';
const FETCH_BOOKING_CART = 'FETCH_BOOKING_CART';
export const UPDATE_BOOKING_CART = 'UPDATE_BOOKING_CART';

const bookingCartQuery = (appContext: AppContext) => `
  bookingId
  attendees {
    id
    firstname
    lastname
    birthday
    unicodeFirstname
    unicodeLastname
    type
  }
  stayServices {
    stayIndex
    childcares {
      id
      productId
      type
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
      attendees {
        id
        schedules {
          price {
            value
          }
          startDate
          endDate
        }
      }
    }
    transfers {
      id
      productId
      type
      label
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
    }
    insurance {
      id
      productId
      type
      label
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
    }
    whiteLabelServices {
      id
      productId
      type
      serviceInfo {
        subtype
        label
      }
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
    }
    rentals {
      id
      productId
      type
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
      attendees {
        id
        schedules {
          price {
            value
          }
          startDate
          endDate
        }
      }
    }
    activities {
      id
      productId
      type
      schedules {
        startDate
        endDate
        attendees {
          id
          price
        }
      }
    }
    pickRoom {
      id
      type
      accommodations {
        id
        rooms {
          id
        }
      }
    }
  }
  price {
    total {
        formattedValue(precision: ${getBookingPrecision(appContext.locale)})
        value
        currency
    }
    currency
    detail {
      service
      amount
    }
  }
  isEmpty
  hasServicesInOption`;

const cartQuery = (appContext: AppContext) => `
  amount {
    total {
      value
      currency
      formattedValue(precision: ${getBookingPrecision(appContext.locale)})
    }
  }
  isEmpty
  cartBookings {
    ${bookingCartQuery(appContext)}
  }`;

export const getBookingsCartQuery = (appContext: AppContext, bookingIds: Array<string>) => {
  const gqlBookingIds = bookingIds.map((id) => `"${id}"`);
  return `
    query {
      bookingsCart(bookingIds : [${gqlBookingIds.toString()}]) {
        ${cartQuery(appContext)}
      }
    }
  `;
};
export const fetchBookingsCart =
  (bookingIds: Array<string>, context: AppContext) => (dispatch: ReduxDispatch) =>
    dispatch(
      createAction(
        FETCH_BOOKING_CART,
        {},
        {
          optimist: true,
          promise: graphQLClient.runNoCacheQuery(
            getBookingsCartQuery(context, bookingIds),
            context,
            {
              requestName: CUSTOMER_ACCOUNT_GET_BOOKINGS_CART,
            },
          ),
        },
      ),
    );
type mutationSchedules = ReadonlyArray<{
  start_date: string;
  end_date: string;
  attendees: ReadonlyArray<{
    id: string;
    isRented?: boolean;
  }>;
}>;
type mutationAccommodations = Array<{
  id: string;
  rooms: Array<{
    id: string;
  }>;
}>;
type addServiceParams = {
  context: AppContext;
  id: string;
  bookingId: string;
  amountToUpdate: number;
  schedules?: mutationSchedules;
  accommodations?: mutationAccommodations;
  action?: string;
  serviceName: ServiceType;
  stayIndex?: number;
};

const addService = ({
  context,
  id,
  bookingId,
  amountToUpdate,
  schedules,
  accommodations,
  serviceName,
  stayIndex,
}: addServiceParams) => {
  return `mutation {
  addServiceCart(
    bookingId : "${bookingId}",
    amount: ${amountToUpdate},
    serviceCart: {
      id: "${id}",
      stay_index: ${stayIndex || 0},
      ${
        schedules
          ? attributesToString({
              schedules,
            })
          : ''
      }
      ${
        accommodations
          ? attributesToString({
              accommodations,
            })
          : ''
      }
    }
    serviceName: "${serviceName}") {
      cart {
        ${bookingCartQuery(context)}
      }
      totalAmount {
        value
        currency
        formattedValue(precision: ${getBookingPrecision(context.locale)})
      }
    }
  }`;
};

export const removeService = (
  appContext: AppContext,
  id: string,
  bookingId: string,
  amountToUpdate: number,
) => {
  return `mutation {
    removeServiceCart(
      bookingId : "${bookingId}",
      amount: ${amountToUpdate},
      itemId: "${id}")
      {
        cart {
          ${bookingCartQuery(appContext)}
        }
        totalAmount {
          value
          currency
          formattedValue
        }
      }
  }`;
};
export type updateCartParams = {
  context: AppContext;
  id: string;
  bookingId: string;
  schedules?: mutationSchedules;
  accommodations?: mutationAccommodations;
  context: AppContext;
  action?: string;
  serviceName: ServiceType;
  stayIndex?: number;
  successMsg?: string;
};
export const updateBookingCart =
  ({
    context,
    id,
    bookingId,
    schedules,
    accommodations,
    serviceName,
    action,
    successMsg,
    stayIndex,
  }: updateCartParams) =>
  (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const bookingsCart = getState()?.memberSpace?.cart?.data?.bookingsCart || {};
    const totalAmount = bookingsCart.amount?.total?.value || 0;
    const bookingPrice = bookingsCart?.cartBookings.find(
      (cartBooking) => cartBooking.bookingId === bookingId,
    ).price.total.value;
    const amountToUpdate = totalAmount - bookingPrice;
    dispatch(
      createAction(
        UPDATE_BOOKING_CART,
        {
          id,
          bookingId,
          toastLabel: successMsg,
        },
        {
          optimist: true,
          promise: graphQLClient.runNoCacheQuery(
            action === 'update'
              ? addService({
                  context,
                  id,
                  bookingId,
                  amountToUpdate,
                  schedules,
                  accommodations,
                  serviceName,
                  stayIndex,
                })
              : removeService(context, id, bookingId, amountToUpdate),
            context,
            {
              requestName:
                action === 'update'
                  ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
                  : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
            },
          ),
        },
      ),
    );
  };
export const updateBookingCartSequencially = (
  params: Array<updateCartParams>,
  cartState?: BookingsCart,
) => {
  if (params.length === 0) {
    return Promise.resolve();
  }

  const { context, id, bookingId, schedules, accommodations, serviceName, action, stayIndex } =
    params[0];
  const totalAmount = cartState?.amount?.total?.value || 0;
  const bookingPrice =
    cartState?.cartBookings.find((cartBooking) => cartBooking.bookingId === bookingId)?.price?.total
      ?.value || 0;
  const amountToUpdate = totalAmount - bookingPrice;
  const promise = graphQLClient.runNoCacheQuery(
    action === 'update'
      ? addService({
          context,
          id,
          bookingId,
          amountToUpdate,
          schedules,
          accommodations,
          serviceName,
          stayIndex,
        })
      : removeService(context, id, bookingId, amountToUpdate),
    context,
    {
      requestName:
        action === 'update'
          ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
          : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
    },
  );
  return promise.then(() => {
    return updateBookingCartSequencially(params.slice(1));
  });
};
/* export const updateBookingCartAll = (params: Array<updateCartParams>, cartState: BookingsCart) => {
  return Promise.all(
    params.map(({ id, bookingId, schedules, accommodations, serviceName, context, action }) =>
      graphQLClient.runNoCacheQuery(
        action === 'update'
          ? addService({ id, bookingId, 0, schedules, accommodations, serviceName })
          : removeService(id, bookingId, 0),
        context,
        {
          requestName:
            action === 'update'
              ? CUSTOMER_ACCOUNT_UPDATE_BOOKING_CART
              : CUSTOMER_ACCOUNT_REMOVE_BOOKING_CART_ITEM,
        },
      ),
    ),
  );
}; */
